import React, { JSX } from 'react'
import { Navigate, useLocation, useParams } from 'react-router'
import { Squares2X2Icon } from '@heroicons/react/24/outline'
import { ChatOverlayManager, QuickOverlayManagerView } from 'components/chatOverlays/ChatOverlayManager'
import { EmoteWallManager } from 'components/chatOverlays/EmoteWallManager'
import { MerchOverlayManager } from 'components/chatOverlays/MerchOverlayManager'
import { TickerBarManager } from 'components/chatOverlays/TickerBarManager'
import { LoadingSpinner } from 'components/Loading'
import { useEntitlements } from 'context/plan'
import { useMerchSurfaceVisible } from 'hooks/useMerchSurfaceVisible'
import { tickerBarPageGuard, useTickerBarSurfaceVisible } from 'hooks/useTickerBarSurfaceVisible'
import { QUICK_OVERLAYS_PATH } from 'lib/paths'

const QUICK_CHATS_BASE = `${QUICK_OVERLAYS_PATH}/chats`
const QUICK_EMOTE_WALLS_BASE = `${QUICK_OVERLAYS_PATH}/emote-walls`
const QUICK_MERCH_BASE = `${QUICK_OVERLAYS_PATH}/merch`
const QUICK_TICKER_BARS_BASE = `${QUICK_OVERLAYS_PATH}/message-tickers`

const QUICK_BACK_LABEL = 'Back to Quick Overlays'

const QuickOverlaysShell = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <div className='flex flex-1' data-testid='quick-overlays-page'>
    <main className='flex flex-1 flex-col py-4'>
      <header className='mb-1 flex items-center gap-3 px-4'>
        <Squares2X2Icon className='size-6 text-dm-text-accent' aria-hidden='true' />
        <h1 className='text-2xl font-semibold text-dm-text-primary'>Quick Overlays</h1>
      </header>
      {children}
    </main>
  </div>
)

const editorView = (pathname: string, id: string | undefined): QuickOverlayManagerView =>
  pathname.endsWith('/new') ? 'create' : { edit: id ?? '' }

export const QuickOverlaysPage = (): JSX.Element => {
  const merchVisible = useMerchSurfaceVisible()
  const tickerBarsVisible = useTickerBarSurfaceVisible() === 'visible'
  const { superuser } = useEntitlements()
  return (
    <QuickOverlaysShell>
      <p className='mb-2 px-4 text-sm text-dm-text-secondary'>
        Standalone overlays intended for use as a browser source in your streaming software
      </p>

      <ChatOverlayManager
        view='list'
        basePath={QUICK_CHATS_BASE}
        backLabel={QUICK_BACK_LABEL}
        sizeControl
      />
      <EmoteWallManager
        view='list'
        basePath={QUICK_EMOTE_WALLS_BASE}
        backLabel={QUICK_BACK_LABEL}
      />
      {tickerBarsVisible && (
        <TickerBarManager
          view='list'
          basePath={QUICK_TICKER_BARS_BASE}
          backLabel={QUICK_BACK_LABEL}
        />
      )}
      {merchVisible && (
        <MerchOverlayManager
          view='list'
          basePath={QUICK_MERCH_BASE}
          backLabel={QUICK_BACK_LABEL}
          superuser={superuser}
        />
      )}
    </QuickOverlaysShell>
  )
}

export const QuickOverlaysChatEditorPage = (): JSX.Element => {
  const { chatId } = useParams<{ chatId: string }>()
  const { pathname } = useLocation()
  return (
    <QuickOverlaysShell>
      <ChatOverlayManager
        view={editorView(pathname, chatId)}
        basePath={QUICK_CHATS_BASE}
        backLabel={QUICK_BACK_LABEL}
        sizeControl
      />
    </QuickOverlaysShell>
  )
}

export const QuickOverlaysEmoteWallEditorPage = (): JSX.Element => {
  const { emoteWallId } = useParams<{ emoteWallId: string }>()
  const { pathname } = useLocation()
  return (
    <QuickOverlaysShell>
      <EmoteWallManager
        view={editorView(pathname, emoteWallId)}
        basePath={QUICK_EMOTE_WALLS_BASE}
        backLabel={QUICK_BACK_LABEL}
      />
    </QuickOverlaysShell>
  )
}

export const QuickOverlaysTickerBarEditorPage = (): JSX.Element => {
  const { tickerBarId } = useParams<{ tickerBarId: string }>()
  const { pathname } = useLocation()
  const guard = tickerBarPageGuard(useTickerBarSurfaceVisible())
  if (guard === 'redirect') return <Navigate to={QUICK_OVERLAYS_PATH} replace />
  if (guard === 'wait') {
    return (
      <QuickOverlaysShell>
        <div className='flex items-center justify-center py-10' data-testid='ticker-editor-pending'>
          <LoadingSpinner />
        </div>
      </QuickOverlaysShell>
    )
  }
  return (
    <QuickOverlaysShell>
      <TickerBarManager
        view={editorView(pathname, tickerBarId)}
        basePath={QUICK_TICKER_BARS_BASE}
        backLabel={QUICK_BACK_LABEL}
      />
    </QuickOverlaysShell>
  )
}

export const QuickOverlaysMerchEditorPage = (): JSX.Element => {
  const { merchOverlayId } = useParams<{ merchOverlayId: string }>()
  const { pathname } = useLocation()
  const { superuser } = useEntitlements()
  const merchVisible = useMerchSurfaceVisible()
  if (!merchVisible) return <Navigate to={QUICK_OVERLAYS_PATH} replace />
  return (
    <QuickOverlaysShell>
      <MerchOverlayManager
        view={editorView(pathname, merchOverlayId)}
        basePath={QUICK_MERCH_BASE}
        backLabel={QUICK_BACK_LABEL}
        superuser={superuser}
      />
    </QuickOverlaysShell>
  )
}
